import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CustomFieldDef, CustomFieldEntity } from '@goyone/shared';
import { api } from '../lib/api';

export type CustomValues = Record<string, string | number | boolean | null>;

/**
 * ما يلزم لرسم خانةٍ فعلًا.
 *
 * الكيانُ والمعرّفُ والترتيب لا تدخل الرسم، فلا تُطلَب. وبهذا ترسم الدالةُ
 * نفسُها استمارةَ التقديم — وحقولُها تُضبط في التوظيف لا في الحقول
 * المخصّصة — بدل نسخةٍ ثانيةٍ تتباعد عن الأولى مع أوّل تعديل.
 */
export type RenderableField = Pick<
  CustomFieldDef,
  'key' | 'label' | 'type' | 'options' | 'required' | 'hint'
>;

/**
 * تعريفات حقول كيان.
 *
 * بلا مهلة تقادم: كنت جعلتها خمس دقائق ظنًّا أن التعريفات نادرة التغيّر —
 * وهي كذلك — لكن أثر ذلك أن مسؤولًا يضيف حقلًا ثم يفتح النموذج فلا يجده،
 * فيظن العطل في النظام. والطلب صغير جدًّا، وثمن إعادته أهون من هذا الشك.
 */
export function useCustomFields(entity: CustomFieldEntity) {
  return useQuery({
    queryKey: ['custom-fields', entity],
    queryFn: () => api<CustomFieldDef[]>(`/custom-fields?entity=${entity}`),
  });
}

/** الفعّالة وحدها تُعرض في النماذج. */
export function activeFields(defs: CustomFieldDef[] | undefined): CustomFieldDef[] {
  return (defs ?? []).filter((f) => f.isActive);
}

/**
 * قيمة الحقل للعرض في خانة إدخال.
 *
 * كل شيء نصّ في خانة الإدخال — حتى الرقم — ويُحوَّل عند الإرسال. الاحتفاظ
 * بالرقم رقمًا في الحالة يجعل مسح الخانة يعطي NaN بدل الفراغ.
 */
export function toFormValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

/**
 * تحويل قيم النموذج إلى ما يُرسَل.
 *
 * الفراغ يُرسل null لا يُحذف: حذفه من الحمولة يعني «اتركه كما هو» في
 * التعديل الجزئي، وهو ما يمنع المستخدم من تفريغ خانة ملأها بالخطأ.
 */
export function toPayload(
  defs: CustomFieldDef[],
  form: Record<string, string>,
): CustomValues {
  const out: CustomValues = {};

  for (const def of defs) {
    const raw = (form[def.key] ?? '').trim();

    if (raw === '') {
      out[def.key] = null;
      continue;
    }

    if (def.type === 'BOOLEAN') out[def.key] = raw === 'true';
    else if (def.type === 'NUMBER') out[def.key] = Number(raw);
    else out[def.key] = raw;
  }

  return out;
}

/**
 * خانات الحقول المخصَّصة داخل نموذج.
 *
 * تُعرض بالترتيب الذي ضبطته الإدارة لا بترتيب إنشائها: ترتيب الخانات جزء
 * من كون النموذج مفهومًا.
 */
export function CustomFieldInputs({
  defs,
  values,
  onChange,
  idPrefix = 'cf',
}: {
  defs: RenderableField[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  idPrefix?: string;
}) {
  if (!defs.length) return null;

  return (
    <>
      {defs.map((def) => {
        const id = `${idPrefix}-${def.key}`;
        const value = values[def.key] ?? '';

        return (
          <div
            key={def.key}
            className={`field${def.type === 'MULTILINE' ? ' span-2' : ''}`}
          >
            <label className={`label${def.required ? ' req' : ''}`} htmlFor={id}>
              {def.label}
            </label>

            {def.type === 'SELECT' ? (
              <select
                id={id}
                className="select"
                value={value}
                onChange={(e) => onChange(def.key, e.target.value)}
              >
                <option value="">اختر</option>
                {def.options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : def.type === 'BOOLEAN' ? (
              <select
                id={id}
                className="select"
                value={value}
                onChange={(e) => onChange(def.key, e.target.value)}
              >
                <option value="">—</option>
                <option value="true">نعم</option>
                <option value="false">لا</option>
              </select>
            ) : def.type === 'MULTILINE' ? (
              <textarea
                id={id}
                className="input"
                rows={3}
                value={value}
                onChange={(e) => onChange(def.key, e.target.value)}
              />
            ) : (
              <input
                id={id}
                className={`input${
                  def.type === 'NUMBER' || def.type === 'DATE' || def.type === 'PHONE' || def.type === 'URL'
                    ? ' num'
                    : ''
                }`}
                type={
                  def.type === 'NUMBER'
                    ? 'number'
                    : def.type === 'DATE'
                      ? 'date'
                      : def.type === 'URL'
                        ? 'url'
                        : 'text'
                }
                dir={def.type === 'PHONE' || def.type === 'URL' ? 'ltr' : undefined}
                inputMode={def.type === 'PHONE' ? 'tel' : undefined}
                placeholder={def.type === 'URL' ? 'https://…' : undefined}
                value={value}
                onChange={(e) => onChange(def.key, e.target.value)}
              />
            )}

            {def.hint && <span className="tiny mute">{def.hint}</span>}
          </div>
        );
      })}
    </>
  );
}

/** عرض القيم المحفوظة في قائمة تعريف — لملف العميل. */
export function CustomFieldList({
  defs,
  values,
}: {
  defs: CustomFieldDef[];
  values: Record<string, unknown> | null | undefined;
}) {
  const bag = values ?? {};
  if (!defs.length) return null;

  return (
    <dl className="detail-list">
      {defs.map((def) => {
        const raw = bag[def.key];
        const text =
          raw === null || raw === undefined || raw === ''
            ? '—'
            : def.type === 'BOOLEAN'
              ? raw === true
                ? 'نعم'
                : 'لا'
              : String(raw);

        return (
          <Fragment key={def.key}>
            <dt>{def.label}</dt>
            <dd className={def.type === 'NUMBER' || def.type === 'DATE' ? 'num' : undefined}>
              {text}
            </dd>
          </Fragment>
        );
      })}
    </dl>
  );
}
